import React, { useMemo } from 'react';
import InputText from '../../../components/agv-forms-input';
import InputRadio from '../../../components/agv-forms-radio';
import InputCombobox from '../../../components/agv-forms-combobox/input-combobox';
import {
  CONSIGNMENT_RULE_TYPES,
  CONSIGNMENT_RULE_TYPE_HINT,
  CONSIGNMENT_WEIGHT_UNIT_OPTIONS,
  getConsignmentRuleTypeOptions,
  productsPerPalletToFixedQuantity,
} from '../../../utils/consignment-rule.util';

export default function ConsignmentRuleFormFields({
  itemKey,
  setItemKey,
  packagingId,
  setPackagingId,
  ruleType,
  setRuleType,
  productsPerPallet,
  setProductsPerPallet,
  weightDivisor,
  setWeightDivisor,
  weightUnit,
  setWeightUnit,
  isActive,
  setIsActive,
  packagingOptions,
  isLinkedPackagingInactive = false,
}) {
  const ruleTypeOptions = useMemo(() => getConsignmentRuleTypeOptions(), []);
  const ruleTypeHint = CONSIGNMENT_RULE_TYPE_HINT[ruleType];

  return (
    <div className="tw-flex tw-flex-col">
      <InputText
        value={itemKey}
        setValue={setItemKey}
        fieldTitle={'Référence produit'}
        isRequired
        emptyError={`Le champ "Référence produit" ne peut être vide.`}
        placeholder={'Référence produit dans les commandes'}
      />

      <InputCombobox
        value={packagingId}
        setValue={setPackagingId}
        fieldTitle={'Type de palette'}
        isRequired
        emptyError={`Le champ "Type de palette" ne peut être vide.`}
        placeholder={'Sélectionner un type de palette'}
        items={packagingOptions}
        style={'tw-mt-4'}
      />

      <InputRadio
        value={ruleType}
        setValue={setRuleType}
        fieldTitle={'Type de règle'}
        items={ruleTypeOptions}
        style={'tw-mt-4'}
      />
      {ruleTypeHint && (
        <p className="tw-text-xs tw-text-text-dark-medium-importance tw-mt-1">
          {ruleTypeHint}
        </p>
      )}

      {ruleType === CONSIGNMENT_RULE_TYPES.FIXED_QUANTITY && (
        <InputText
          value={productsPerPallet}
          setValue={setProductsPerPallet}
          fieldTitle={'Nombre de produits par palette'}
          isRequired
          emptyError={`Le champ "Nombre de produits par palette" ne peut être vide.`}
          placeholder={'Ex : 40'}
          style={'tw-mt-4'}
        />
      )}

      {ruleType === CONSIGNMENT_RULE_TYPES.WEIGHT_FORMULA && (
        <>
          <InputText
            value={weightDivisor}
            setValue={setWeightDivisor}
            fieldTitle={'Diviseur de poids'}
            isRequired
            emptyError={`Le champ "Diviseur de poids" ne peut être vide.`}
            placeholder={'Ex : 2000'}
            style={'tw-mt-4'}
          />
          <InputRadio
            value={weightUnit}
            setValue={setWeightUnit}
            fieldTitle={'Unité de poids de référence pour la formule'}
            items={CONSIGNMENT_WEIGHT_UNIT_OPTIONS}
            style={'tw-mt-4'}
          />
        </>
      )}

      <InputRadio
        value={isActive}
        setValue={setIsActive}
        fieldTitle={'Statut'}
        items={[
          {
            name: 'Actif',
            value: 'true',
            disabled: isLinkedPackagingInactive,
          },
          { name: 'Inactif', value: 'false' },
        ]}
        style={'tw-mt-4'}
      />
      {isLinkedPackagingInactive && (
        <p className="tw-text-xs tw-text-red-500 tw-mt-1">
          Le type de palette associé est inactif. La règle ne peut pas être
          réactivée tant que la palette reste inactive.
        </p>
      )}
    </div>
  );
}

export function buildConsignmentRulePayload({
  itemKey,
  packagingId,
  ruleType,
  productsPerPallet,
  weightDivisor,
  weightUnit,
  isActive,
}) {
  const payload = {
    item_key: itemKey.trim(),
    packaging_id: Number(packagingId),
    rule_type: ruleType,
    is_active: isActive === 'true',
  };

  if (ruleType === CONSIGNMENT_RULE_TYPES.WEIGHT_FORMULA) {
    payload.weight_divisor = Number(weightDivisor);
    payload.weight_unit = weightUnit;
  }

  if (
    ruleType === CONSIGNMENT_RULE_TYPES.FIXED_QUANTITY &&
    productsPerPallet !== '' &&
    productsPerPallet != null
  ) {
    // Le client raisonne en « N produits = 1 palette » ; l'API attend le
    // multiplicateur fixed_quantity = 1/N.
    const fixedQuantity = productsPerPalletToFixedQuantity(productsPerPallet);
    if (fixedQuantity != null) {
      payload.fixed_quantity = fixedQuantity;
    }
  }

  return payload;
}

export function isConsignmentRuleFormValid({
  itemKey,
  packagingId,
  ruleType,
  productsPerPallet,
  weightDivisor,
  weightUnit,
}) {
  if (!itemKey?.trim() || !packagingId || !ruleType) return false;

  if (ruleType === CONSIGNMENT_RULE_TYPES.FIXED_QUANTITY) {
    const n = Number(productsPerPallet);
    return productsPerPallet !== '' && Number.isInteger(n) && n > 0;
  }

  if (ruleType === CONSIGNMENT_RULE_TYPES.WEIGHT_FORMULA) {
    const divisor = Number(weightDivisor);
    return (
      weightDivisor !== '' &&
      !Number.isNaN(divisor) &&
      divisor > 0 &&
      !!weightUnit
    );
  }

  return true;
}
